import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useEffect, useRef, useState } from "react";
import LoadingEllipsis from "../LoadingEllipsis";
import useCampaignsService from "../../services/CampaignsService";
import { Campaign, Rule, RuleGroup } from "../../types/Campaigns";
import NeutralButton from "../NeutralButton";
import PositiveButton from "../PositiveButton";
import NegativeButton from "../NegativeButton";
import TitlePicker from "../Title/TitlePicker";
import { HttpError } from "../../types/Error";
import { describeCampaignRule } from "../../utilities/CampaignRuleUtilities";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";

interface RuleGroupOverlayFormProps {
  campaignId?: string;
  rewardId?: string;
  ruleGroupId?: string;
  onClose?: (updated: boolean) => void;
  onUpdated?: () => void;
}

export default function RuleGroupOverlayForm({
  campaignId,
  rewardId,
  ruleGroupId,
  onClose,
  onUpdated,
}: RuleGroupOverlayFormProps) {
  const {
    getCampaign,
    getRuleGroupsForReward,
    getRulesForRuleGroup,
    deleteRuleGroup,
    deleteRule,
    updateRule,
    updateRuleGroup,
    createRuleGroup,
    createRule,
  } = useCampaignsService();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rules, setRules] = useState<Rule[]>([]);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [campaignTitleId, setCampaignTitleId] = useState<string>("");
  const [ruleValidationErrors, setRuleValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  const currentRuleGroupId = useRef<string | undefined>(ruleGroupId);
  const [ruleFormOpen, setRuleFormOpen] = useState(false);

  const [ruleGroupFormData, setRuleGroupFormData] = useState({
    logic: "AND",
  });

  const [ruleFormData, setRuleFormData] = useState({
    id: "",
    type: "duration",
    comparator: ">=",
    targetValue: 0,
    titleId: "",
  });

  const validateRuleForm = () => {
    const errors: { [key: string]: string } = {};

    if (!ruleFormData.type) {
      errors.type = "Rule type is required.";
    }

    if (!ruleFormData.comparator) {
      errors.comparator = "Comparator is required.";
    }

    if (ruleFormData.targetValue <= 0) {
      errors.targetValue = "Target value must be greater than zero.";
    }

    setRuleValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const loadCampaign = async () => {
    if (!campaignId) {
      setCampaign(null);
      return;
    }
    try {
      setIsLoading(true);
      const fetchedCampaign = await getCampaign(campaignId);
      setCampaign(fetchedCampaign as Campaign);
      if (fetchedCampaign && fetchedCampaign.titleId) {
        setCampaignTitleId(fetchedCampaign.titleId);
      }
    } catch (error) {
      console.error("Error loading campaign:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRules = async () => {
    if (!campaign || !rewardId || !currentRuleGroupId.current) {
      setRules([]);
      return;
    }

    setIsLoading(true);
    try {

      const ruleGroups = await getRuleGroupsForReward(campaignId!, rewardId!);
      const matchedGroup = (ruleGroups as RuleGroup[]).find((group) => group.id === currentRuleGroupId.current);

      if (matchedGroup) {
        setRuleGroupFormData({
          logic: matchedGroup.logic,
        });
      }

      const fetchedRules = await getRulesForRuleGroup(
        campaign.id,
        rewardId,
        currentRuleGroupId.current
      );
      const loadedRules: Rule[] = (fetchedRules as Rule[]) || [];
      setRules(loadedRules);
    } catch (error) {
      console.error("Error loading rules:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const validationError = (fieldName: string): null | string => {
    return ruleValidationErrors[fieldName] || null;
  };

  const validStyle = (fieldName: string) => {
    // Find the ruleValidationErrors key with the fieldName.
    let valid = Object.keys(ruleValidationErrors).indexOf(fieldName) === -1;

    return valid ? "" : "border-red-800";
  };

  const handleRuleInputChange = (e: any) => {
    const { name, value } = e.target;

    setRuleFormData({
      ...ruleFormData,
      [name]: value,
    });
  };

  const saveChangeToRuleGroupLogic = async (newLogic: string) => {
    if (currentRuleGroupId.current) {
      setIsSaving(true);
      try {
        const newGroupData: RuleGroup = {
          id: currentRuleGroupId.current,
          isActive: true,
          logic: newLogic,
          priority: 1,
        };
        await updateRuleGroup(
          campaignId!,
          rewardId!,
          currentRuleGroupId.current,
          newGroupData
        );

        onUpdated?.();
      } catch (error) {
        console.error("Error updating rule group logic:", error);
      } finally {
        setIsSaving(false);
      }
    }
  }

  const handleRuleGroupLogicChange = (e: any) => {
    const { value } = e.target;
    setRuleGroupFormData({
      ...ruleGroupFormData,
      logic: value,
    });

    if (currentRuleGroupId.current) {
      saveChangeToRuleGroupLogic(value);
    }
  };

  const openRule = (rule: Rule) => {
    setRuleFormData({
      id: rule.id,
      type: rule.type,
      comparator: rule.comparator,
      targetValue: rule.targetValue,
      titleId: rule.titleId ?? "",
    });
  };

  const startNewRule = () => {
    // Clear the rule form data
    setRuleFormData({
      id: "",
      type: "duration",
      comparator: ">=",
      targetValue: 0,
      titleId: campaignTitleId || "",
    });

    setRuleFormOpen(true);
  };

  const saveRuleGroup = async () => {
    setIsSaving(true);

    try {
      var RuleGroup: RuleGroup = {
        id: currentRuleGroupId.current || "",
        isActive: true,
        logic: ruleGroupFormData.logic,
        priority: 1,
      };

      if (currentRuleGroupId.current) {
        await updateRuleGroup(
          campaignId!,
          rewardId!,
          currentRuleGroupId.current,
          RuleGroup
        );
      } else {
        const createdRuleGroup = await createRuleGroup(
          campaignId!,
          rewardId!,
          RuleGroup
        );

        currentRuleGroupId.current = (createdRuleGroup as RuleGroup).id;
      }
    } catch (error) {
      console.error("Error saving rule group:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const saveRule = async () => {
    if (!validateRuleForm()) {
      return;
    }

    try {
      await saveRuleGroup();
      setIsSaving(true);

      var newFormData = {
        ...ruleFormData,
        metric: "",
        targetValue: Number(ruleFormData.targetValue),
        priority: 1,
        uniquenessKey: "",
        aggregation: "",
        occurenceTarget: Number(ruleFormData.targetValue),
      };

      if (
        newFormData.type === "duration" ||
        newFormData.type === "all_streams_duration"
      ) {
        newFormData = {
          ...newFormData,
          metric: "minutes",
          aggregation: "sum",
          targetValue: Math.floor(newFormData.targetValue),
          occurenceTarget: Math.floor(newFormData.targetValue),
        };
      }

      if (newFormData.type === "unique_days" || newFormData.type === "streak") {
        newFormData = {
          ...newFormData,
          metric: "unique_days",
          aggregation: "count_distinct",
        };
      }

      if (newFormData.type === "stream_count") {
        newFormData = {
          ...newFormData,
          metric: "streams",
          aggregation: "count",
        };
      }

      const rule: Rule = {
        id: newFormData.id,
        type: newFormData.type,
        parameters: "",
        isActive: true,
        metric: newFormData.metric,
        comparator: newFormData.comparator,
        targetValue: newFormData.targetValue,
        aggregation: newFormData.aggregation,
        titleId: newFormData.titleId || undefined,
        occurrenceTarget: newFormData.occurenceTarget,
        priority: 1,
      };

      var updatedRule: Rule | null | HttpError = null;

      if (ruleFormData.id) {
        updatedRule = await updateRule(
          campaignId!,
          rewardId!,
          currentRuleGroupId.current!,
          ruleFormData.id,
          rule
        );
      } else {
        updatedRule = await createRule(
          campaignId!,
          rewardId!,
          currentRuleGroupId.current!,
          rule
        );

        setRuleFormData((prev) => ({
          ...prev,
          id: (updatedRule as Rule).id,
        }));
      }

      setRuleFormOpen(false);
      loadRules();
    } catch (error) {
      console.error("Error saving rule:", error);
    } finally {
      setIsSaving(false);
      onUpdated?.();
    }
  };

  const deleteOneRule = async (ruleId: string) => {
    if (!campaign || !rewardId || !currentRuleGroupId.current) {
      return;
    }
    setIsSaving(true);
    try {
      await deleteRule(
        campaign.id,
        rewardId,
        currentRuleGroupId.current,
        ruleId
      );

      // Remove the rule from the local state
      setRules((prev) => prev.filter((rule) => rule.id !== ruleId));

      // If there are no more rules, delete the rule group and close the form.
      if (rules.length <= 1) {
        await deleteRuleGroup(
          campaign.id,
          rewardId,
          currentRuleGroupId.current
        );
        onClose?.(true);
      }
    } catch (error) {
      console.error("Error deleting rule:", error);
    } finally {
      setIsSaving(false);
      loadRules();
      onUpdated?.();
    }
  };

  const saveRuleForm = (e: React.FormEvent) => {
    e.preventDefault();
    saveRule();
  };

  const getTargetValueLabel = () => {
    switch (ruleFormData.type) {
      case "duration":
        return "Target Duration (in minutes)";
      case "all_streams_duration":
        return "Target Total Duration (in minutes)";
      case "unique_days":
        return "Target Unique Days";
      case "streak":
        return "Target Streak Days";
      case "stream_count":
        return "Target Stream Count";
      default:
        return "Target Value";
    }
  };

  const deleteThisGroup = async () => {
    if (!campaign || !rewardId || !currentRuleGroupId.current) {
      return;
    }
    setIsSaving(true);
    try {
      await deleteRuleGroup(
        campaign.id,
        rewardId,
        currentRuleGroupId.current
      );
    } catch (error) {
      console.error("Error deleting rule group:", error);
    } finally {
      setIsSaving(false);
      onUpdated?.();
      onClose?.(true);
    }
  };

  useEffect(() => {

    if (ruleGroupId === undefined) {
      startNewRule();
      return;
    }

    loadRules();
  }, [campaign, rewardId, ruleGroupId]);

  useEffect(() => {
    loadCampaign();
  }, [campaignId]);

  return (
    <Dialog
      open={true}
      onClose={() => {onClose?.(false);}}
      className="relative z-10"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-x-hidden overflow-y-auto rounded-lg bg-slate-900 text-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            {isLoading ? (
              <>
                <h2 className="text-lg font-medium">
                  Loading rules
                  <LoadingEllipsis />
                </h2>
              </>
            ) : (
              <>
                {ruleFormOpen ? (
                  <>
                    <h2 className="text-lg font-medium">
                      {ruleFormData.id ? "Edit Rule" : "Add Rule"}
                    </h2>
                    <form
                      onSubmit={saveRuleForm}
                      className="form flex flex-col gap-4"
                    >
                      <div>
                        <label htmlFor="type" className="block leading-6">
                          Rule Type
                        </label>
                        <select
                          id="type"
                          value={ruleFormData.type}
                          name="type"
                          onChange={handleRuleInputChange}
                          className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                            "type"
                          )}`}
                        >
                          <option value="">Select a rule type</option>
                          <option value="duration">
                            Duration (any single stream)
                          </option>
                          <option value="all_streams_duration">
                            All Streams Duration
                          </option>
                          <option value="unique_days">
                            Unique Days streamed on
                          </option>
                          <option value="streak">
                            Streak of days streamed
                          </option>
                          <option value="stream_count">
                            Stream count (all single streams count)
                          </option>
                        </select>
                        {validationError("type") && (
                          <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                            {validationError("type")}
                          </div>
                        )}
                      </div>
                      <div>
                        <label htmlFor="comparator" className="block leading-6">
                          Comparator
                        </label>
                        <select
                          id="comparator"
                          value={ruleFormData.comparator}
                          name="comparator"
                          onChange={handleRuleInputChange}
                          className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                            "comparator"
                          )}`}
                        >
                          <option value="">Select a comparator</option>
                          <option value=">">Greater than (&gt;)</option>
                          <option value=">=">
                            Greater than or equal to (&gt;=)
                          </option>
                          <option value="==">Equal to (==)</option>
                        </select>
                        {validationError("comparator") && (
                          <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                            {validationError("comparator")}
                          </div>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="targetValue"
                          className="block leading-6"
                        >
                          {getTargetValueLabel()}
                        </label>
                        <input
                          type="number"
                          id="targetValue"
                          name="targetValue"
                          value={ruleFormData.targetValue}
                          onChange={handleRuleInputChange}
                          className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                            "targetValue"
                          )}`}
                        />
                        {validationError("targetValue") && (
                          <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                            {validationError("targetValue")}
                          </div>
                        )}
                      </div>
                      <div>
                        <TitlePicker
                          label="Game title - important. If left empty, rule will apply to all titles."
                          value={ruleFormData.titleId}
                          required={false}
                          disabled={isSaving}
                          placeholder="Type to search for a title"
                          error={validationError("titleId")}
                          onChange={(id) => {
                            console.log("TitlePicker onChange:", id);
                            setRuleFormData((prev) => ({
                              ...prev,
                              titleId: id || "",
                            }));
                          }}
                        />

                        {validationError("titleId") && (
                          <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                            {validationError("titleId")}
                          </div>
                        )}
                      </div>
                      <div className="sm:col-span-4 flex items-center gap-2 justify-end mt-4">
                        <NeutralButton
                          className=""
                          onClick={() => {
                            setRuleFormOpen(false);
                            if (!ruleFormData.id) {
                              // If no rule ID, we need to delete the empty rule
                              onClose?.(true);
                            }
                          }}
                        >
                          Cancel
                        </NeutralButton>
                        <PositiveButton
                          className=""
                          type="submit"
                          disabled={isSaving}
                        >
                          {isSaving ? (
                            <>
                              Saving
                              <LoadingEllipsis />
                            </>
                          ) : (
                            <>Save</>
                          )}
                        </PositiveButton>
                      </div>
                    </form>
                  </>
                ) : (
                  <>
                    <h2 className="text-lg font-medium">
                      Rules for this reward
                    </h2>
                    <form className="form flex flex-col gap-4">
                      <div>
                        <label htmlFor="logic" className="block leading-6">
                          Rule Group Logic
                        </label>
                        <select
                          id="logic"
                          value={ruleGroupFormData.logic}
                          onChange={handleRuleGroupLogicChange}
                          className={`w-full px-3 py-2 border rounded text-black`}
                        >
                          <option value="AND">
                            AND (all rules must match)
                          </option>
                          <option value="OR">OR (any rule can match)</option>
                        </select>
                      </div>
                    </form>
                    <h4 className="mt-6 mb-2 text-md font-medium">Rules</h4>
                    {rules.length === 0 ? (
                      <p>No rules defined for this rule group.</p>
                    ) : (
                      <ul className="list-none list-inside space-y-2">
                        {rules.map((rule) => (
                          <li
                            key={rule.id}
                            className="flex items-center justify-between bg-gray-50 text-black rounded-lg px-3 py-2"
                          >
                            <span className="text-sm text-gray-800">
                              {describeCampaignRule(rule)}
                            </span>
                            <span className="flex flex-row gap-3 shrink-0 ml-4">
                              <button
                               disabled={isSaving}
                                className=""
                                onClick={() => {
                                  openRule(rule);
                                  setRuleFormOpen(true);
                                }}
                              >
                                <EditIcon className="text-cyan-600 hover:text-cyan-800" />
                              </button>
                              <button disabled={isSaving} className="" onClick={() => deleteOneRule(rule.id)}>
                                <DeleteIcon className="text-red-600 hover:text-red-800" />
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="flex flex-row gap-4 mt-6 justify-end">
                      <NeutralButton  disabled={isSaving} className="" onClick={startNewRule}>
                        Add Rule
                      </NeutralButton>
                      {currentRuleGroupId.current && (
                        <NegativeButton
                          disabled={isSaving}
                          className=""
                          onClick={() => deleteThisGroup()}
                        >
                          Delete rule group
                        </NegativeButton>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
